import { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCircleExclamation,
  faClipboardCheck,
  faPlay,
  faSpinner,
  faTriangleExclamation,
} from '@fortawesome/free-solid-svg-icons';
import {
  approveProviderMigration,
  isClientAbort,
  runProviderMigration,
  type ProviderMigrationRunResultDto,
  type ProviderMigrationSnapshot,
} from '../api/provider-migrations.api';
import { parseMigrationError, type ParsedMigrationError } from '../migration-errors';
import { MigrationErrorNotice } from './MigrationErrorNotice';

/**
 * Execute a planned provider migration — approve, then copy and verify.
 *
 * ── THE ONE RULE THIS FILE EXISTS TO ENFORCE ───────────────────────────────
 * `approveProviderMigration()` and `runProviderMigration()` are each called
 * from EXACTLY ONE place: the confirm handler below, which cannot run until a
 * human has opened the dialog and pressed the confirm button. There is
 * deliberately:
 *
 *   • no `useEffect` in this file — nothing fires on mount, render or update;
 *   • no polling, `setInterval`, `setTimeout`, retry loop or background task;
 *   • no auto-approve, no auto-run, no call on the panel becoming visible;
 *   • no path from loading or re-rendering the page to a mutation.
 *
 * Those absences are the enforcement. A reviewer can confirm it by grepping
 * this file for either function name — one call site each.
 *
 * ── TWO SEPARATE OPERATIONS, NEVER ONE HIDDEN MUTATION ─────────────────────
 * The button says "Approve & Run" because that is the operator's intent, but
 * the two backend calls stay distinct and STRICTLY SEQUENTIAL: run is issued
 * only after approve has returned successfully. If approve fails, run is never
 * attempted. If approve succeeds and run fails, NEITHER is retried — the
 * migration is left at READY and the panel switches to its run-only mode so a
 * human can decide, rather than silently re-approving something already
 * approved (which the server would refuse anyway).
 *
 * ── WHY READY HAS ITS OWN MODE ─────────────────────────────────────────────
 * The server accepts `approve` only from `DRY_RUN_COMPLETE`, and `run` only
 * from `READY` or `PAUSED`. A migration interrupted between the two calls is
 * therefore stranded at `READY` with no way forward unless the UI offers a
 * run-only action. It does — and it never re-approves.
 *
 * `PAUSED` is deliberately NOT handled here: a paused run means items failed
 * verification, which needs an operator to diagnose, not a retry button.
 */

/** The statuses this panel can act on. Anything else renders nothing. */
export const EXECUTABLE_STATUSES = ['DRY_RUN_COMPLETE', 'READY'] as const;

export interface ExecuteMigrationPanelProps {
  migrationId: string;
  /** `DRY_RUN_COMPLETE` (approve then run) or `READY` (run only). */
  status: string;
  totalObjects: number | null;
  /** The planning snapshot, for the operator's own scope check. */
  snapshot: ProviderMigrationSnapshot | null;
  /** Called with the server's run result so the page can reload and report. */
  onExecuted: (result: ProviderMigrationRunResultDto) => void;
  /**
   * Fired the instant the run is ISSUED, not when it returns.
   *
   * The run is inline on the server and can take many minutes, so waiting for
   * the response before showing progress is what left the page blank. This opens
   * the live view immediately, and it is what makes a browser-side timeout
   * harmless: the copy stays visible even if this request never resolves.
   */
  onRunStarted?: () => void;
}

function fmtBytes(bytes: number | null | undefined): string {
  if (bytes === null || bytes === undefined) return '—';
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let value = bytes / 1024;
  let i = 0;
  while (value >= 1024 && i < units.length - 1) {
    value /= 1024;
    i += 1;
  }
  return `${value.toFixed(value >= 100 ? 0 : 1)} ${units[i]}`;
}

export function ExecuteMigrationPanel({
  migrationId,
  status,
  totalObjects,
  snapshot,
  onExecuted,
  onRunStarted,
}: ExecuteMigrationPanelProps) {
  const [confirming, setConfirming] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  /**
   * Which of the two calls failed, in the operator's terms. Held separately from
   * the parsed refusal because the RECOVERY differs — a failed approval copied
   * nothing, a failed run leaves the migration at READY — while the server's own
   * reason is rendered verbatim either way.
   */
  const [errorPrefix, setErrorPrefix] = useState<string | null>(null);
  const [error, setError] = useState<ParsedMigrationError | null>(null);
  /**
   * Set when the browser gave up on the run request but the SERVER did not.
   * This is not a failure and must never be presented as one — the copy is still
   * executing and the live view below is tracking it.
   */
  const [detached, setDetached] = useState(false);
  /** Set when approve succeeded but run did not: the panel drops to run-only. */
  const [approved, setApproved] = useState(false);

  // READY means a previous attempt already approved. Never approve again.
  const runOnly = status === 'READY' || approved;

  const planned = totalObjects ?? snapshot?.plannedAssetCount ?? null;
  const plannedBytes = snapshot?.plannedBytes ?? null;
  const zeroCopy = snapshot?.totals?.alreadyOnDestination ?? null;
  const blocked = snapshot?.totals?.blocked ?? null;

  /**
   * The ONLY call site for either mutation. Reachable solely from the confirm
   * button inside the dialog, which is itself reachable solely from an
   * explicit click.
   */
  const confirmExecute = async () => {
    if (submitting) return; // no duplicate submissions
    setSubmitting(true);
    setError(null);
    setErrorPrefix(null);
    setDetached(false);
    // Tracked locally, NOT read back from state: `setApproved` does not update
    // the value this closure captured, so a run failure would otherwise be
    // reported as an approval failure — and the recovery differs.
    let approvalDone = runOnly;
    // Whether the RUN request actually left the browser. Distinguishes "the
    // server is still copying" from "approve failed and nothing started".
    let runIssued = false;
    try {
      if (!runOnly) {
        // STEP 1. If this throws, step 2 is never reached.
        await approveProviderMigration(migrationId);
        approvalDone = true;
        setApproved(true);
      }
      // STEP 2 — only after a successful approval (or an already-READY one).
      // The live view opens BEFORE the await: the server begins copying as soon
      // as it receives this, and the operator should see it happen rather than
      // stare at a spinner until the whole run returns.
      onRunStarted?.();
      runIssued = true;
      const result = await runProviderMigration(migrationId);
      setConfirming(false);
      onExecuted(result);
    } catch (err) {
      // A client-side abort is NOT a server failure. The request was dropped by
      // the browser; the server never heard about it and is still copying. Say
      // exactly that, and offer NO retry — retrying here would be an attempt to
      // start a second concurrent run over the same objects.
      //
      // `runIssued` is essential: a network failure during APPROVE must never be
      // reported as "the run is still executing", because in that case no run was
      // ever started. Only an abort of the run request itself means work is
      // continuing on the server.
      if (runIssued && isClientAbort(err)) {
        setDetached(true);
        setConfirming(false);
        onRunStarted?.();
        setSubmitting(false);
        return;
      }
      // The server's structured refusal is shown in full — code and reason
      // included — instead of the transport's "Request failed with status code
      // 409". No automatic retry of either call: a failed execution is a
      // decision for the operator.
      setError(parseMigrationError(err));
      setErrorPrefix(
        runOnly
          ? 'Run failed. Nothing was copied by this attempt.'
          : approvalDone
            ? 'Approval succeeded, but the run failed. The migration is now READY; you can run it without approving again.'
            : 'Approval failed. Nothing was copied.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="rounded-xl border border-border bg-card p-5" data-testid="execute-panel">
      <h2 className="flex items-center gap-2 text-sm font-semibold text-foreground">
        <FontAwesomeIcon icon={faClipboardCheck} className="h-4 w-4 text-muted-foreground" />
        {runOnly ? 'Run migration' : 'Dry run complete — ready to execute'}
      </h2>
      <p className="mt-1 text-sm text-muted-foreground" data-testid="execute-nothing-copied">
        {runOnly
          ? 'This migration is approved and awaiting execution. '
          : 'The scope has been calculated and '}
        <strong className="text-foreground">nothing has been copied yet</strong>.
      </p>

      <dl className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="execute-scope">
        <div className="rounded-lg border border-border p-3">
          <dt className="text-xs uppercase tracking-wide text-muted-foreground">Objects planned</dt>
          <dd className="mt-0.5 text-lg font-semibold text-foreground">{planned ?? '—'}</dd>
        </div>
        <div className="rounded-lg border border-border p-3">
          <dt className="text-xs uppercase tracking-wide text-muted-foreground">
            Already on destination
          </dt>
          <dd className="mt-0.5 text-lg font-semibold text-foreground">
            {zeroCopy ?? '—'}
            {zeroCopy !== null && (
              <span className="ml-1 text-xs font-normal text-muted-foreground">zero-copy</span>
            )}
          </dd>
        </div>
        <div className="rounded-lg border border-border p-3">
          <dt className="text-xs uppercase tracking-wide text-muted-foreground">
            Bytes to transfer
          </dt>
          <dd className="mt-0.5 text-lg font-semibold text-foreground">{fmtBytes(plannedBytes)}</dd>
        </div>
        <div className="rounded-lg border border-border p-3">
          <dt className="text-xs uppercase tracking-wide text-muted-foreground">Blockers</dt>
          <dd
            className={
              blocked
                ? 'mt-0.5 text-lg font-semibold text-amber-600'
                : 'mt-0.5 text-lg font-semibold text-foreground'
            }
          >
            {blocked ?? 0}
          </dd>
        </div>
      </dl>

      {detached && (
        <div
          className="mt-4 rounded-lg border border-sky-500/30 bg-sky-500/5 p-3"
          data-testid="execute-detached"
        >
          <p className="text-sm font-medium text-sky-700">
            The run is still executing on the server.
          </p>
          <p className="mt-1 text-sm text-muted-foreground">
            The browser stopped waiting for the request, but that does not stop or cancel the copy —
            the server never sees the disconnect. Live progress is shown below and updates on its
            own. <strong className="text-foreground">Do not start the run again</strong>; a second
            run would be refused while this one holds the migration.
          </p>
        </div>
      )}

      {error && (
        <div className="mt-4" data-testid="execute-error">
          {errorPrefix !== null && (
            <p className="flex items-start gap-2 text-sm font-medium text-destructive">
              <FontAwesomeIcon icon={faCircleExclamation} className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{errorPrefix}</span>
            </p>
          )}
          <MigrationErrorNotice error={error} testId="execute-error-notice" />
        </div>
      )}

      {!confirming && (
        <button
          type="button"
          data-testid="execute-open"
          onClick={() => {
            setError(null);
            setErrorPrefix(null);
            setConfirming(true);
          }}
          className="mt-4 inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground"
        >
          <FontAwesomeIcon icon={faPlay} className="h-3.5 w-3.5" />
          {runOnly ? 'Run Migration' : 'Approve & Run Migration'}
        </button>
      )}

      {confirming && (
        <div
          role="alertdialog"
          aria-modal="true"
          aria-labelledby="execute-confirm-title"
          className="mt-4 rounded-lg border border-amber-500/40 bg-amber-500/5 p-4"
          data-testid="execute-confirm"
        >
          <p
            id="execute-confirm-title"
            className="flex items-center gap-2 text-sm font-semibold text-amber-700"
          >
            <FontAwesomeIcon icon={faTriangleExclamation} className="h-4 w-4" />
            {runOnly ? 'Confirm run' : 'Confirm approval and run'}
          </p>

          <ul
            className="mt-3 list-disc space-y-1.5 pl-5 text-sm text-foreground"
            data-testid="execute-confirm-points"
          >
            {!runOnly && (
              <li>
                Approval changes the migration from <strong>DRY_RUN_COMPLETE</strong> to{' '}
                <strong>READY</strong>.
              </li>
            )}
            <li>
              Running will <strong>copy the planned objects</strong> to the destination provider.
            </li>
            <li>
              <strong>This performs real storage writes</strong> for the planned objects.
            </li>
            <li>
              Objects already present on the destination and{' '}
              <strong>checksum-identical will be reused without uploading</strong>.
            </li>
            <li>
              <strong>Ownership does NOT change by running.</strong> Every asset keeps its current
              provider; the ownership transition is a separate, separately authorized step.
            </li>
            <li>
              After a successful run the migration enters <strong>VERIFYING</strong>. It is not
              complete and is not finalized.
            </li>
          </ul>

          <div className="mt-4 flex items-center gap-3">
            <button
              type="button"
              data-testid="execute-confirm-submit"
              disabled={submitting}
              onClick={() => void confirmExecute()}
              className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:cursor-not-allowed disabled:opacity-50"
            >
              {submitting && <FontAwesomeIcon icon={faSpinner} spin className="h-3.5 w-3.5" />}
              {submitting
                ? runOnly
                  ? 'Running…'
                  : 'Approving & running…'
                : runOnly
                  ? 'Run Migration'
                  : 'Approve & Run Migration'}
            </button>
            <button
              type="button"
              data-testid="execute-confirm-cancel"
              disabled={submitting}
              onClick={() => setConfirming(false)}
              className="rounded-lg border border-border px-4 py-2 text-sm text-foreground disabled:opacity-50"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
